import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Info, X, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface InlineTipProps {
  type?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  content: string | React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export const InlineTip: React.FC<InlineTipProps> = ({
  type = 'info',
  title,
  content,
  dismissible = false,
  onDismiss,
  className,
  action,
}) => {
  const [isDismissed, setIsDismissed] = useState(false);

  if (isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  const iconMap = {
    info: Info,
    success: CheckCircle,
    warning: AlertTriangle,
    error: AlertCircle,
  };

  const variantMap = {
    info: 'border-blue-200 bg-blue-50',
    success: 'border-green-200 bg-green-50',
    warning: 'border-amber-200 bg-amber-50',
    error: 'border-red-200 bg-red-50',
  };

  const iconColorMap = {
    info: 'text-blue-600',
    success: 'text-green-600',
    warning: 'text-amber-600',
    error: 'text-red-600',
  };

  const Icon = iconMap[type];

  return (
    <Alert className={cn(variantMap[type], className)}>
      <Icon className={cn('h-4 w-4', iconColorMap[type])} />
      <div className="flex-1">
        {title && (
          <AlertTitle className={cn('mb-1', iconColorMap[type])}>
            {title}
          </AlertTitle>
        )}
        <AlertDescription className={cn('text-sm', iconColorMap[type])}>
          {typeof content === 'string' ? (
            <p className="whitespace-pre-wrap">{content}</p>
          ) : (
            content
          )}
        </AlertDescription>
        {action && (
          <div className="mt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={action.onClick}
              className={cn('text-xs', iconColorMap[type])}
            >
              {action.label}
            </Button>
          </div>
        )}
      </div>
      {dismissible && (
        <button
          onClick={handleDismiss}
          className="ml-auto text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </Alert>
  );
};

